import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigation, useScrollReveal } from '../../hooks';
import { REGIONS } from '../../constants';
import { Button } from '../ui';
import './Booking.css';

/**
 * Booking section component
 * Tab-based region selection with city details
 */
const Booking: React.FC = () => {
	const { t } = useTranslation();
	const { goToCaliforniaBooking, goToTexasBooking, goToFloridaBooking } = useAppNavigation();
	const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
	const [activeTab, setActiveTab] = useState(REGIONS[0].id);

	const handleBooking = (regionId: string) => {
		switch (regionId) {
			case 'california':
				goToCaliforniaBooking();
				break;
			case 'texas':
				goToTexasBooking();
				break;
			case 'florida':
				goToFloridaBooking();
				break;
		}
	};

	const getRegionName = (regionId: string): string => {
		const key = `booking.${regionId}` as const;
		return t(key);
	};

	const getRegionIcon = (regionId: string): string => {
		const icons: Record<string, string> = {
			california: '🌴',
			texas: '🤠',
			florida: '🌊',
		};
		return icons[regionId] || '📍';
	};

	const activeRegion = REGIONS.find((r) => r.id === activeTab) || REGIONS[0];

	return (
		<section
			className="booking"
			id="booking"
			ref={ref as React.RefObject<HTMLElement>}
		>
			<div className={`booking-container ${isVisible ? 'visible' : ''}`}>
				<div className="booking-header">
					<h2>{t('booking.title', 'Book Your Event')}</h2>
					<p>{t('booking.selectRegion', 'Select your region to get started')}</p>
				</div>

				{/* Tab Navigation */}
				<div className="region-tabs">
					{REGIONS.map((region) => (
						<button
							key={region.id}
							className={`region-tab ${activeTab === region.id ? 'active' : ''}`}
							onClick={() => setActiveTab(region.id)}
							type="button"
						>
							<span className="tab-icon">{getRegionIcon(region.id)}</span>
							<span className="tab-name">{getRegionName(region.id)}</span>
						</button>
					))}
				</div>

				{/* Active Region Content */}
				<div className="region-content">
					<div className="region-info">
						<div className="region-icon-large">{getRegionIcon(activeRegion.id)}</div>
						<h3>{getRegionName(activeRegion.id)}</h3>
						<p className="region-tagline">
							{t(`booking.${activeRegion.id}Tagline`, 'Professional hibachi at your doorstep')}
						</p>
					</div>

					<div className="cities-grid">
						<h4>{t('booking.servingAreas', 'Areas We Serve')}</h4>
						<div className="cities-list">
							{activeRegion.cities.map((city, index) => (
								<span key={index} className="city-tag">
									{city}
								</span>
							))}
						</div>
					</div>

					<div className="region-cta">
						<Button
							variant="primary"
							size="lg"
							onClick={() => handleBooking(activeRegion.id)}
						>
							🎉 {t('booking.bookIn', `Book in ${getRegionName(activeRegion.id)}`)}
						</Button>
						<p className="booking-note">
							{t('booking.note', '* No payment required. We will contact you to confirm.')}
						</p>
					</div>
				</div>

				{/* Quick Links */}
				<div className="quick-links">
					<span>{t('booking.quickBook', 'Quick Book:')}</span>
					{REGIONS.map((region) => (
						<button
							key={region.id}
							className="quick-link"
							onClick={() => handleBooking(region.id)}
							type="button"
						>
							{getRegionIcon(region.id)} {getRegionName(region.id)}
						</button>
					))}
				</div>
			</div>
		</section>
	);
};

export default Booking;